import { useTranslations } from 'next-intl'
import { PropsWithChildren } from 'react'

import { RoomThumbnail, TRoomDetail } from '@/entities/room'
import { Link } from '@/shared/i18n'
import { Button, Drawer } from '@/shared/ui'

type Props = Pick<TRoomDetail, 'otherHostRooms'>

export const RoomDrawerTrigger = ({
  children,
  otherHostRooms,
}: PropsWithChildren<Props>) => {
  return (
    <Drawer.Root>
      <Drawer.Trigger asChild>{children}</Drawer.Trigger>
      <RoomDrawer otherHostRooms={otherHostRooms} />
    </Drawer.Root>
  )
}

export const RoomDrawer = ({ otherHostRooms }: Props) => {
  const t = useTranslations('room_detail')

  return (
    <Drawer.Content className="flex max-h-[95dvh] flex-col gap-0 p-0">
      <Drawer.Header className="px-4 pb-5">
        <Drawer.Title>{t('landlord_profile.other_unit')}</Drawer.Title>
        <Drawer.Description className="sr-only">
          {t('landlord_profile.other_unit')}
        </Drawer.Description>
      </Drawer.Header>
      <div className="flex grow flex-col overflow-y-auto px-5.5">
        <RoomList otherHostRooms={otherHostRooms} />
      </div>
      <Drawer.Footer className="px-4 py-6">
        <Drawer.Close asChild>
          <Button size="large" color="secondary">
            {t('landlord_profile.button.close')}
          </Button>
        </Drawer.Close>
      </Drawer.Footer>
    </Drawer.Content>
  )
}

type RoomListProps = Pick<TRoomDetail, 'otherHostRooms'>

export const RoomList = ({ otherHostRooms }: RoomListProps) => {
  const t = useTranslations('room_detail')

  return (
    <ul className="grid grid-cols-3 gap-x-5 gap-y-4 md:grid-cols-[repeat(5,134px)] md:justify-between md:gap-x-0">
      {otherHostRooms.map(room => (
        <li key={room.rid} className="w-25 md:w-[134px]">
          <Link
            href={`/guest/room/${room.rid}`}
            target="_blank"
            className="space-y-2"
          >
            <RoomThumbnail
              src={room.picMain}
              alt={t('landlord_profile.unit_image', {
                roomName: room.roomName,
              })}
              ratio="1:1"
              size={100}
              rounded
              style={{ width: '100%' }}
              imageStyle={{ width: '100%' }}
            />
            <h4 className="typo-label2 line-clamp-1 font-normal text-gray-900">
              {room.roomName}
            </h4>
          </Link>
        </li>
      ))}
    </ul>
  )
}
